import React, { useState } from 'react';
import { Truck, Package, Users, X } from 'lucide-react';
import type { UserRole } from '../../types/profiles';

interface RoleSelectorProps {
  onSelect: (role: UserRole, companyName: string) => void;
  onClose: () => void;
  loading?: boolean;
}

const ROLES = [
  {
    id: 'shipper' as UserRole,
    title: 'Shipper / Farmer',
    description: 'Post loads and manage shipments of cattle, hay, and grain',
    icon: Package,
    color: 'bg-blue-50 border-blue-200 hover:border-blue-400',
    iconColor: 'text-blue-600',
  },
  {
    id: 'carrier' as UserRole,
    title: 'Carrier / Trucker',
    description: 'Find and haul loads with GPS tracking and route management',
    icon: Truck,
    color: 'bg-green-50 border-green-200 hover:border-green-400',
    iconColor: 'text-green-600',
  },
  {
    id: 'broker' as UserRole,
    title: 'Broker',
    description: 'Match shippers with carriers and manage freight operations',
    icon: Users,
    color: 'bg-amber-50 border-amber-200 hover:border-amber-400',
    iconColor: 'text-amber-600',
  },
];

export default function RoleSelector({ onSelect, onClose, loading }: RoleSelectorProps) {
  const [selectedRole, setSelectedRole] = useState<UserRole | null>(null);
  const [companyName, setCompanyName] = useState('');

  const handleSubmit = () => {
    if (!selectedRole) return;
    onSelect(selectedRole, companyName);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg animate-fadeIn">
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Welcome to RanchWorkUSA</h2>
            <p className="text-gray-500 mt-1">Select your role to get started</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="h-5 w-5 text-gray-400" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {ROLES.map((role) => {
            const Icon = role.icon;
            const isSelected = selectedRole === role.id;
            return (
              <button
                key={role.id}
                onClick={() => setSelectedRole(role.id)}
                className={`w-full flex items-start gap-4 p-4 rounded-xl border-2 transition-all text-left ${
                  isSelected
                    ? 'border-green-500 bg-green-50 ring-2 ring-green-200'
                    : role.color
                }`}
              >
                <div className={`p-3 rounded-lg ${isSelected ? 'bg-green-100' : 'bg-white'}`}>
                  <Icon className={`h-6 w-6 ${isSelected ? 'text-green-600' : role.iconColor}`} />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{role.title}</h3>
                  <p className="text-sm text-gray-500 mt-0.5">{role.description}</p>
                </div>
              </button>
            );
          })}

          {selectedRole && (
            <div className="pt-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Company / Business Name (optional)
              </label>
              <input
                type="text"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="e.g., Smith Ranch, ABC Trucking"
                className="w-full rounded-lg border border-gray-300 px-4 py-2.5 focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>
          )}
        </div>

        <div className="p-6 border-t border-gray-100">
          <button
            onClick={handleSubmit}
            disabled={!selectedRole || loading}
            className="w-full py-3 bg-green-600 text-white font-semibold rounded-xl hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? 'Setting up...' : 'Continue'}
          </button>
        </div>
      </div>
    </div>
  );
}
